import { Card, Form, Input, Modal, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { ContainerModalAssinatura, Title } from "../styles";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import {
  type InabilitarCotaUsuarioResponse,
  type InabilitarCotaUsuarioStatus,
  inabilitarCotaUsuarioService,
} from "../../../services/inabilitarCotaUsuarioService";

const statusOptions: Array<{
  label: string;
  value: InabilitarCotaUsuarioStatus;
  motivo: string;
}> = [
  {
    label: "Distrato",
    value: "DISTRATO",
    motivo: "Distrato",
  },
  {
    label: "Vendido pelo proprio",
    value: "VENDIDA_PROPRIO",
    motivo: "Vendido pelo proprio",
  },
  {
    label: "Vendido pela Multicotas",
    value: "VENDIDA_MC",
    motivo: "Vendido pela Multicotas",
  },
  {
    label: "Erro de inclusao",
    value: "ERRO_INCLUSAO",
    motivo: "Erro de inclusao",
  },
];

interface ModalInabilitarCotaProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  cotaUsuarioId: number;
  onSuccess: (response: InabilitarCotaUsuarioResponse) => void;
}

const getErrorMessage = (error: any) => {
  if (typeof error?.message === "string") {
    return error.message;
  }

  if (Array.isArray(error?.message)) {
    return error.message.join(", ");
  }

  if (typeof error?.error === "string") {
    return error.error;
  }

  return "Nao foi possivel inabilitar a cota.";
};

export default function ModalInabilitarCota({
  isModalOpen,
  handleFechar,
  cotaUsuarioId,
  onSuccess,
}: ModalInabilitarCotaProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { showError, contextHolder } = useMessage();
  const { TextArea } = Input;
  const selectedStatus = Form.useWatch<InabilitarCotaUsuarioStatus>("status", form);

  const statusOptionsMap = useMemo(
    () =>
      statusOptions.reduce<Record<string, string>>((acc, option) => {
        if (option.motivo) {
          acc[option.value] = option.motivo;
        }

        return acc;
      }, {}),
    []
  );

  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
    }
  }, [form, isModalOpen]);

  const handleStatusChange = (value: InabilitarCotaUsuarioStatus) => {
    form.setFieldsValue({
      status: value,
      motivo: statusOptionsMap[value],
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);

      const response = await inabilitarCotaUsuarioService(cotaUsuarioId, {
        status: values.status,
        motivo: statusOptionsMap[values.status],
        observacoes: values.observacoes?.trim() || undefined,
        metadata: {
          origem: "painel-admin",
        },
      });

      form.resetFields();
      handleFechar();
      onSuccess(response);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      showError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      cancelText="Cancelar"
      okText="Inabilitar cota"
      onOk={handleSubmit}
      confirmLoading={isLoading}
      okButtonProps={{
        danger: true,
      }}
    >
      {contextHolder}
      <Card
        title={<Title>Inabilitar Cota</Title>}
        style={{
          width: "100%",
          marginTop: "20px",
          borderRadius: "10px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <ContainerModalAssinatura>
          <Form form={form} layout="vertical" name="inabilitar-cota">
            <Form.Item
              name="status"
              label="Motivo da inabilitacao"
              rules={[
                {
                  required: true,
                  message: "Selecione o motivo da inabilitacao.",
                },
              ]}
            >
              <Select
                placeholder="Selecione o motivo"
                options={statusOptions.map(({ label, value }) => ({
                  label,
                  value,
                }))}
                onChange={handleStatusChange}
              />
            </Form.Item>

            <Form.Item name="motivo" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="observacoes"
              label="Observacoes"
              rules={[
                {
                  validator: async (_, value) => {
                    if (
                      selectedStatus !== "ERRO_INCLUSAO" ||
                      (typeof value === "string" && value.trim())
                    ) {
                      return;
                    }

                    throw new Error(
                      "Informe as observacoes para erro de inclusao."
                    );
                  },
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Detalhes adicionais sobre a inabilitacao da cota"
              />
            </Form.Item>
          </Form>
        </ContainerModalAssinatura>
      </Card>
    </Modal>
  );
}
